'use client';

import { type ReactNode, useState } from 'react';

import { EligibilityResult } from '@dte/otw-azure-functions/src/common/interfaces/api/EligibilityResult';
import type { PremiseDataDocument } from '@dte/otw-azure-functions/src/common/interfaces/containers/premises/PremiseData';
import {
	getPremiseEligibilityResult,
	hasElectricService,
	hasOnlyStreetLightService,
	hasStreetLightService,
} from '@dte/otw-azure-functions/src/common/premises/data/attributes/eligibility/getPremiseEligibilityResult';
import { EmergencyDisconnectReason } from '@dte/otw-azure-functions/src/common/services/emergencyDisconnect/EmergencyDisconnectReason';

import { ErrorAlert } from '../../../../../../components/Alerts/ErrorAlert';
import { InfoAlert } from '../../../../../../components/Alerts/InfoAlert';
import { WarningAlert } from '../../../../../../components/Alerts/WarningAlert';
import { AuthenticatedJsonContent } from '../../../content/jsonGet/AuthenticatedJsonContent';
import { getPremiseGPS } from '../address/getPremiseGPS';
import { getPremiseMasterDataURL } from '../status/getPremiseMasterDataURL';
import type { PropsWithPremiseId } from '../usePremiseId';
import { UtilitySearchResult } from './UtilitySearchResult';

export const EligibilityMessages: Record<EligibilityResult, ReactNode> = {
	[EligibilityResult.ElectricActive]: 'Electric service is active',
	[EligibilityResult.DNP]: 'Premise is in DNP state',
	[EligibilityResult.ElectricInactive]: 'Electric service is inactive',
	[EligibilityResult.GasOnly]: 'Premise has only gas service',
	[EligibilityResult.NoService]: 'Premise has no metered service',
	[EligibilityResult.NotFound]: 'Premise not found',
};

function EmergencyDisconnectContent({ masterData }: { masterData: PremiseDataDocument }): ReactNode {
	const emergencyDisconnect = masterData.data?.reason === EmergencyDisconnectReason;
	if (!emergencyDisconnect) {
		return null;
	}

	return <ErrorAlert>Emergency disconnect requested</ErrorAlert>;
}

/**
 * Content to display if the premise does not have electric service
 *
 * @param param0
 * @returns
 */
function NoElectricContent({ masterData }: { masterData: PremiseDataDocument }): ReactNode {
	const hasElectric = hasElectricService(masterData);
	if (hasElectric) {
		return null;
	}

	const code = getPremiseEligibilityResult(masterData);
	if (!code) {
		return null;
	}

	const message = EligibilityMessages[code];
	const location = getPremiseGPS(masterData);

	return (
		<WarningAlert>
			<p>{message}</p>
			<UtilitySearchResult
				location={location}
				premiseId={masterData.premiseId}
				connectionId={masterData.data.address.connectionId}
			/>
		</WarningAlert>
	);
}

function StreetLightContent({ masterData }: { masterData: PremiseDataDocument }): ReactNode {
	const streetLights = hasStreetLightService(masterData);
	if (!streetLights) {
		return null;
	}

	const streetLightOnly = hasOnlyStreetLightService(masterData);
	if (streetLightOnly) {
		return (
			<WarningAlert>
				<p>Premise has only street / traffic lights</p>
			</WarningAlert>
		);
	}

	return (
		<InfoAlert>
			<p>Premise has street / traffic lights</p>
		</InfoAlert>
	);
}

/**
 * Content to display if the premise has electric service
 *
 * @param param0
 * @returns
 */
function ElectricContent({ masterData }: { masterData: PremiseDataDocument }): ReactNode {
	const hasElectric = hasElectricService(masterData);
	if (!hasElectric) {
		return null;
	}

	const code = getPremiseEligibilityResult(masterData);
	if (!code) {
		return null;
	}

	// TODO: include details for street light only
	if (code === EligibilityResult.ElectricActive) {
		return null;
	}

	const message = EligibilityMessages[code];
	if (!message) {
		return null;
	}

	return (
		<WarningAlert>
			{message}
			{/* <br />
			<hr />
			<NearbyActivePremises
				premiseId={masterData.premiseId}
				connectionId={masterData.data.address.connectionId}
			/> */}
		</WarningAlert>
	);
}

function PremiseEligibilityContent({ masterData }: { masterData: PremiseDataDocument | undefined }): ReactNode {
	if (!masterData) {
		return null;
	}

	return (
		<>
			<EmergencyDisconnectContent masterData={masterData} />
			<NoElectricContent masterData={masterData} />
			<ElectricContent masterData={masterData} />
			<StreetLightContent masterData={masterData} />
		</>
	);
}

export function PremiseEligibility({ premiseId }: PropsWithPremiseId): ReactNode {
	const url = getPremiseMasterDataURL(premiseId);
	const [masterData, setMasterData] = useState<PremiseDataDocument>();

	return (
		<AuthenticatedJsonContent url={url} setData={setMasterData} hasData={Boolean(masterData?.data)}>
			<PremiseEligibilityContent masterData={masterData} />
		</AuthenticatedJsonContent>
	);
}
